import { Suspense } from "react";
import {
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { PinDrop, Quiz, EventNote, Refresh } from "@mui/icons-material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { PAGES } from "../constants";
import { colors } from "./V2Reporting";
import { useV2Sites } from "./V2SitesAPI";
import { useV2SiteStatus } from "./V2SiteStatusAPI";

const BAD_IR_REF = 100000;
interface V2SiteSummaryProps {
  siteId: string;
}

export function V2SiteSummary({ siteId }: V2SiteSummaryProps) {
  return (
    <Suspense fallback={<CircularProgress />}>
      <V2SiteSummaryBody siteId={siteId} />
    </Suspense>
  );
}

// Route wrapper that reads :siteId from URL
export function V2SiteSummaryRoute() {
  const { siteId } = useParams();
  if (!siteId) {
    return (
      <Typography variant="h5" alignSelf={"flex-start"}>
        Select a site to view its summary.
      </Typography>
    );
  }
  return <V2SiteSummary siteId={siteId} />;
}

function V2SiteSummaryBody({ siteId }: V2SiteSummaryProps) {
  const { sites, loading: sitesLoading } = useV2Sites();
  const { data: siteStatusData, loading: siteStatusLoading, error: siteStatusError, fetchSiteStatus } = useV2SiteStatus(siteId);

  // Find the site by ID
  const site = sites.find(s => s._id === siteId);

  // Show loading while sites are being fetched
  if (sitesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!site) {
    return (
      <Typography variant="h4" alignSelf={"flex-start"}>
        Site not found (ID: {siteId})
      </Typography>
    );
  }

  function getColorClass(base: 'green' | 'yellow' | 'red', state: string): string {
    const isManufacturing = state === 'MANUFACTURING';
    const suffix = isManufacturing ? '-cell-light' : '-cell';
    return `${base}${suffix}`;
  }

  function getStateColor(state: string): string {
    switch (state) {
      case "IN_FIELD":
      case "MANUFACTURING":
        return getColorClass('green', state);
      default:
        return getColorClass('red', state);
    }
  }


  function formatDate(dateString: string): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  }


  // Function to get color class based on reporting time
  function getReportingColorForBoard(lastReading: string, state: string): string {
    if (!lastReading) return getColorClass('red', state);

    const now = Date.now();
    const lastReadingTime = new Date(lastReading).getTime();
    const oneDay = 24 * 60 * 60 * 1000;
    const threeDays = 3 * oneDay;

    if (now - lastReadingTime < oneDay) {
      return getColorClass('green', state);
    } else if (now - lastReadingTime < threeDays) {
      return getColorClass('yellow', state);
    } else {
      return getColorClass('red', state);
    }
  }

  // Create DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 80,
      sortable: true,
    },
    {
      field: 'tcmReporting',
      headerName: 'Reporting',
      width: 155,
      sortable: true,
      renderCell: (params) => formatDate(params.row.last_reading),
      cellClassName: (params) => getReportingColorForBoard(params.row.last_reading, params.row.state),
    },
    {
      field: 'state',
      headerName: 'State',
      width: 120,
      sortable: true,
      renderCell: (params) => params.row.state,
      cellClassName: (params) => getStateColor(params.row.state),
    },
    {
      field: 'device',
      headerName: 'Device',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <a
          href={`https://liora-grafana.tail781803.ts.net/d/eeeqooqrq13wgc/blueboards-v2?orgId=1&var-boardLabel=${params.row.tcm_devuid|| ""}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'white',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          <Tooltip title={params.row.tcm_devuid} arrow>
            <span style={{ cursor: 'pointer' }}>{params.row.device}</span>
          </Tooltip>
        </a>
      ),
      cellClassName: (params) => params.row.device? getColorClass('green', params.row.state) : getColorClass('red', params.row.state),
    },
    {
      field: 'gateway_id',
      headerName: 'Gateway',
      width: 170,
      sortable: true,
      renderCell: (params) =>
        params.row.gateway_id ? (
          <a
            href={`/gateways?stype=eui&value=${encodeURIComponent(params.row.gateway_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'white',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {params.row.gateway_id}
          </a>
        ) : (
          "-"
        ),
      cellClassName: (params) => getReportingColorForBoard(params.row.last_reading, params.row.state)
    },
    {
      field: 'battery_voltage_v',
      headerName: 'BV',
      width: 70,
      sortable: true,
      renderCell: (params) => params.row.battery_voltage_v ?? "-",
      cellClassName: (params) => getReportingColorForBoard(params.row.last_reading, params.row.state),

    },
    {
      field: 'snr',
      headerName: 'SNR',
      width: 80,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={`RSSI: ${params.row.rssi ?? "-"}`} arrow>
          <span style={{ cursor: 'pointer' }}>{params.row.snr ?? "-"}</span>
        </Tooltip>
      ),
      cellClassName: (params) => getReportingColorForBoard(params.row.last_reading, params.row.state),

    },
    {
      field: 'firmware',
      headerName: 'Firmware',
      width: 80,
      sortable: true,
      renderCell: (params) => params.row.firmware ?? "-",
      cellClassName: (params) => getReportingColorForBoard(params.row.last_reading, params.row.state),

    },
    {
      field: 'humidity',
      headerName: 'Humidity',
      width: 120,
      sortable: true,
      renderCell: (params) => params.row.humidity || "-",
      cellClassName: (params) => {
        if (params.row.humidity === null || params.row.humidity === undefined) {
          return getColorClass('red', params.row.state);
        }
        return getReportingColorForBoard(params.row.last_reading, params.row.state);
      },

    },
    {
      field: 'pressure_millibar',
      headerName: 'Pressure (mbar)',
      width: 130,
      sortable: true,
      renderCell: (params) => params.row.pressure_millibar ?? "-",
      cellClassName: (params) => {
        if (params.row.pressure_millibar === null || params.row.pressure_millibar === undefined) {
          return getColorClass('red', params.row.state);
        }
        return getReportingColorForBoard(params.row.last_reading, params.row.state);
      },

    },
    {
      field: 'temperature_celsius',
      headerName: 'Temp (°C)',
      width: 110,
      sortable: true,
      renderCell: (params) => params.row.temperature_celsius ?? "-",
      cellClassName: (params) => {
        if (params.row.temperature_celsius === null || params.row.temperature_celsius === undefined) {
          return getColorClass('red', params.row.state);
        }
        return getReportingColorForBoard(params.row.last_reading, params.row.state);
      },

    },
    {
      field: 'board1Reporting',
      headerName: 'Reporting',
      width: 155,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[0]?.last_reading ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return "";
        return formatDate(board.last_reading);
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board1Firmware',
      headerName: 'Firmware',
      width: 80,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[0]?.firmware ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return "";
        return board.firmware;
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return '';
        if (!board.last_reading) return 'red-cell';
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board1Humidity',
      headerName: 'Humidity',
      width: 130,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[0]?.humidity ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return "";
        return (
          <Tooltip title={`${board.type || 'Unknown'}`} arrow>
            <span style={{ cursor: 'pointer' }}>{board.humidity ?? "-"}</span>
          </Tooltip>
        );
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (board.humidity == null) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board1IrRef',
      headerName: 'IR Ref',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[0]?.ir_ref ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return "";
        return board.ir_ref || "-";
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[0];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (!board.ir_ref) return getColorClass('red', params.row.state);
        if (board.ir_ref < BAD_IR_REF) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board2Reporting',
      headerName: 'Reporting',
      width: 155,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[1]?.last_reading ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return "";
        return formatDate(board.last_reading);
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board2Firmware',
      headerName: 'Firmware',
      width: 80,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[1]?.firmware ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return "";
        return board.firmware;
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board2Humidity',
      headerName: 'Humidity',
      width: 130,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[1]?.humidity ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return "";
        return (
          <Tooltip title={`${board.type || 'Unknown'}`} arrow>
            <span style={{ cursor: 'pointer' }}>{board.humidity ?? "-"}</span>
          </Tooltip>
        );
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (board.humidity == null) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);
      },
    },
    {
      field: 'board2IrRef',
      headerName: 'IR Ref',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[1]?.ir_ref ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return "";
        return board.ir_ref || "-";
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[1];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (!board.ir_ref) return getColorClass('red', params.row.state);
        if (board.ir_ref < BAD_IR_REF) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);

      },
    },
    {
      field: 'board3Reporting',
      headerName: 'Reporting',
      width: 155,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[2]?.last_reading ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return "";
        return formatDate(board.last_reading);
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);

      },
    },
    {
      field: 'board3Firmware',
      headerName: 'Firmware',
      width: 80,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[2]?.firmware ?? '',
      renderCell: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return "";
        return board.firmware;
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);

      },
    },
    {
      field: 'board3Humidity',
      headerName: 'Humidity',
      width: 130,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[2]?.humidity ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return "";
        return (
          <Tooltip title={`${board.type || 'Unknown'}`} arrow>
            <span style={{ cursor: 'pointer' }}>{board.humidity ?? "-"}</span>
          </Tooltip>
        );
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (board.humidity == null) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);

      },
    },
    {
      field: 'board3IrRef',
      headerName: 'IR Ref',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => row.boards?.[2]?.ir_ref ?? 0,
      renderCell: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return "";
        return board.ir_ref || "-";
      },
      cellClassName: (params) => {
        const board = params.row.boards?.[2];
        if (!board) return '';
        if (!board.last_reading) return getColorClass('red', params.row.state);
        if (!board.ir_ref ) return getColorClass('red', params.row.state);
        if (board.ir_ref < BAD_IR_REF) return getColorClass('red', params.row.state);
        return getReportingColorForBoard(board.last_reading, params.row.state);

      },
    },
  ];

  const coordinates = site.location?.coordinates;
  const latitude = typeof coordinates?.[1] === 'number' ? coordinates[1] : undefined;
  const longitude = typeof coordinates?.[0] === 'number' ? coordinates[0] : undefined;

  return (
    <>
      <Typography variant="h4" alignSelf={"flex-start"}>
        {site.name}
        {latitude !== undefined && longitude !== undefined && (
          <Tooltip title="Maps">
            <IconButton>
              <a
                href={`https://www.google.com/maps/search/${latitude},${longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PinDrop />
              </a>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Testing Boards">
          <IconButton>
            <RouterLink to={`${PAGES.TESTING_BOARDS}?site_id=${site._id}`}>
              <Quiz />
            </RouterLink>
          </IconButton>
        </Tooltip>
        <Tooltip title="Field Notes">
          <IconButton>
            <RouterLink to={`${PAGES.NOTES_SITE_FIELD}?site=${site._id}`}>
              <EventNote />
            </RouterLink>
          </IconButton>
        </Tooltip>
        <Tooltip title="Refetch">
          <IconButton onClick={() => fetchSiteStatus()}>
            <Refresh color="secondary" sx={{ cursor: 'pointer' }} />
          </IconButton>
        </Tooltip>
      </Typography>

      {siteStatusLoading && <CircularProgress />}
      {siteStatusError && (
        <Typography color="error" variant="body2">
          Error loading site status: {siteStatusError}
        </Typography>
      )}

      <Box sx={{
        height: "70vh",
        width: '100%',
        overflow: 'hidden'
      }}>
        <DataGrid
          rows={siteStatusData}
          columns={columns}
          getRowId={(row) => row.quantifier_mongo_id}
          disableRowSelectionOnClick
          hideFooterPagination
          disableColumnMenu
          columnGroupingModel={[
            {
              groupId: 'TCM',
              headerAlign: 'center',
              children: [
                {
                  field: 'name',
                },
                {
                  field: 'tcmReporting',
                },
                {
                  field: 'state',
                },
                {
                  field: 'device',
                },
                {
                  field: 'gateway_id',
                },
                {
                  field: 'battery_voltage_v',
                },
                {
                  field: 'snr',
                },
                {
                  field: 'firmware',
                },
                {
                  field: 'humidity',
                },
                {
                  field: 'pressure_millibar',
                },
                {
                  field: 'temperature_celsius',
                },
              ],
            },
            {
              groupId: 'Board 1',
              headerAlign: 'center',
              children: [
                {
                  field: 'board1Reporting',
                },
                {
                  field: 'board1Firmware',
                },
                {
                  field: 'board1Humidity',
                },
                {
                  field: 'board1IrRef',
                },
              ],
            },
            {
              groupId: 'Board 2',
              headerAlign: 'center',
              children: [
                {
                  field: 'board2Reporting',
                },
                {
                  field: 'board2Firmware',
                },
                {
                  field: 'board2Humidity',
                },
                {
                  field: 'board2IrRef',
                },
              ],
            },
            {
              groupId: 'Board 3',
              headerAlign: 'center',
              children: [
                {
                  field: 'board3Reporting',
                },
                {
                  field: 'board3Firmware',
                },
                {
                  field: 'board3Humidity',
                },
                {
                  field: 'board3IrRef',
                },
              ],
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: siteStatusData?.length || 100 },
            },
          }}
          sx={{
            '& .green-cell': {
              backgroundColor: `${colors.GREEN} !important`,
            },
            '& .yellow-cell': {
              backgroundColor: `${colors.YELLOW} !important`,
            },
            '& .red-cell': {
              backgroundColor: `${colors.RED} !important`,
            },
            '& .green-cell-light': {
              backgroundColor: `${colors.GREEN_LIGHT} !important`,
            },
            '& .yellow-cell-light': {
              backgroundColor: `${colors.YELLOW_LIGHT} !important`,
            },
            '& .red-cell-light': {
              backgroundColor: `${colors.RED_LIGHT} !important`,
            },
            m: 1
          }}
        />
      </Box>
    </>
  );
}
